import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BusinessSetupService } from 'src/app/_services/business-setup.service';
import { BusinessSetupProgressService } from 'src/app/_services/business-setup-progress.service';
import { ConnMessageService } from 'src/app/_services/conn-message.service';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { FormDataService } from 'src/app/_services/form-data.service';
import { GlobalService } from 'src/app/_services/global.service';
import { PK_PROVINCE_OPTIONS, countryOfBusiness } from 'src/app/_data/country-states';
import {
  CarrierId,
  SCV2_CARRIERS,
  SCV2_UPS_ENABLED,
  SCV2_USPS_SERVICES,
  ShippingConfigV2Service,
  ShippingZoneV2,
} from '../../shipping-config-v2.service';

@Component({
  selector: 'app-shipping-rules-v2',
  templateUrl: './shipping-rules-v2.component.html',
  styleUrls: ['./shipping-rules-v2.component.scss'],
})
export class ShippingRulesV2Component implements OnInit, OnDestroy {
  @Output() goToCarriers = new EventEmitter<void>();

  carriers = SCV2_CARRIERS;
  // USPS-only for first version. UPS is kept in code but gated behind SCV2_UPS_ENABLED. TODO re-enable UPS.
  upsEnabled = SCV2_UPS_ENABLED;
  carrierIds: CarrierId[] = SCV2_UPS_ENABLED ? ['ups', 'usps'] : ['usps'];

  rateTypes: { id: 'flat' | 'free' | 'live'; label: string }[] = [
    { id: 'flat', label: 'Flat Rate' },
    { id: 'free', label: 'Free Shipping' },
    { id: 'live', label: 'Calculated Rates' },
  ];

  uspsServices = SCV2_USPS_SERVICES;
  usStates: { name: string; code: string }[] = [];

  /** A Pakistani business can also ship within Pakistan (all provinces), priced in PKR. Others: USA only. */
  readonly isPk = countryOfBusiness(GlobalService.userInfo?.businessSetup?.[0], GlobalService.countryCode) === 'PK';
  readonly countryOptions: { value: string; label: string }[] = this.isPk
    ? [{ value: 'PK', label: 'Pakistan' }, { value: 'USA', label: 'USA' }]
    : [{ value: 'USA', label: 'USA' }];
  readonly currencyCode = this.isPk ? 'PKR' : 'USD';
  readonly currencySymbol = this.isPk ? 'Rs.' : '$';
  readonly pkProvinces = PK_PROVINCE_OPTIONS.map((p) => ({ name: p.name, code: p.code }));

  /** The states (USA) or provinces (Pakistan) a zone can cover. */
  zoneStates(zone: ShippingZoneV2): { name: string; code: string }[] {
    return zone.countries?.[0] === 'PK' ? this.pkProvinces : this.usStates;
  }

  zoneRegionWord(zone: ShippingZoneV2): string {
    return zone.countries?.[0] === 'PK' ? 'provinces' : 'states';
  }

  /** Changing the country starts the zone over with every state / province of the new country selected. */
  onCountryChange(zone: ShippingZoneV2): void {
    zone.states = this.zoneStates(zone).map((s) => s.code);
    zone.allStates = true;
  }

  zones: ShippingZoneV2[] = [];
  loading = false;
  saving = false;
  // true once the user has attempted a save — flips on the inline per-section error messages.
  submitted = false;
  // zones already persisted server-side → save uses PUT (update) instead of POST (create).
  private originalZonesCount = 0;
  private destroy$ = new Subject<void>();

  constructor(
    public service: ShippingConfigV2Service,
    private businessSetupService: BusinessSetupService,
    private businessSetupProgressService: BusinessSetupProgressService,
    private connMessageService: ConnMessageService,
    private appBaseDataService: AppBaseDataService,
    private formDataService: FormDataService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    // Component is *ngIf'd on the rules sub-tab, so ngOnInit fires each time the tab opens.
    this.loadUsStates();
    this.loadShippingConfiguration();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get connections() {
    return this.service.connections;
  }

  get anyConnected(): boolean {
    return !!(this.connections.usps || (this.upsEnabled && this.connections.ups));
  }

  carrierSubtitle(id: CarrierId): string {
    const conn = this.connections[id];
    if (conn) {
      return conn.mode === 'api' ? 'API connected — live rates' : 'Manual mode — flat per service';
    }
    // No carrier-connection requirement (V1 parity): rates are calculated at checkout.
    return 'Rates calculated at checkout';
  }

  // ---- data loading ----

  private loadUsStates(): void {
    this.formDataService.getUsStates()
      .pipe(takeUntil(this.destroy$))
      .subscribe((states: any[]) => {
        this.usStates = (states || []).map((s) => ({ name: s.name, code: s.code || s.id }));
      });
  }

  loadShippingConfiguration(): void {
    this.loading = true;
    this.businessSetupService.getShippingConfiguration()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any) => {
          this.loading = false;
          let apiZones: any[] = [];
          if (res?.success && res.data) {
            apiZones = Array.isArray(res.data) ? res.data : (res.data.zones || []);
          }
          this.originalZonesCount = apiZones.length;
          this.zones = apiZones.map((z, i) => this.mapApiZoneToModel(z, i === 0));
        },
        () => {
          // No saved config (or endpoint not reachable): start empty, let the user add a zone.
          this.loading = false;
          this.originalZonesCount = 0;
          this.zones = [];
        },
      );
  }

  private mapApiZoneToModel(zone: any, expanded: boolean): ShippingZoneV2 {
    const states = this.extractStateCodes(zone?.states);
    const countries = zone?.countries?.length ? zone.countries : ['USA'];
    const regionCount = countries[0] === 'PK' ? this.pkProvinces.length : this.usStates.length;
    return {
      id: zone?.id ?? null,
      zoneName: zone?.zoneName || 'Zone',
      isEnabled: zone?.isEnabled !== false,
      expanded,
      countries,
      states,
      allStates: states.length === 0 || states.length >= regionCount,
      rateType: this.determineRateType(zone),
      flatCost: Number(zone?.flatRate?.rate) || 0,
      pickup: zone?.storePickUp?.isEnablePickup === true,
      pickupCost: Number(zone?.storePickUp?.pickupCost) || 0,
      uspsEnabled: zone?.usps?.isEnabledUsps !== false,
      uspsServices: Array.isArray(zone?.usps?.enabledServices) ? zone.usps.enabledServices : [],
      markup: '0',
      fallback: '9.00',
      flatRateId: zone?.flatRate?.id ?? null,
      storePickUpId: zone?.storePickUp?.id ?? null,
      uspsId: zone?.usps?.id ?? null,
      uspsUserID: zone?.usps?.userID ?? null,
    };
  }

  private extractStateCodes(statesObject: any): string[] {
    if (!statesObject) return [];
    const codes: string[] = [];
    Object.values(statesObject).forEach((arr: any) => {
      if (Array.isArray(arr)) codes.push(...arr);
    });
    return codes;
  }

  private determineRateType(zone: any): 'flat' | 'free' | 'live' {
    const t = Number(zone?.shippingtype ?? zone?.shippingType ?? 0);
    if (t === 1) return 'flat';
    if (t === 2) return 'free';
    if (t === 3 || t === 4) return 'live';
    if (zone?.usps?.isEnabledUsps === true) return 'live';
    if (zone?.flatRate?.isEnableFlat === true) return 'flat';
    return 'free';
  }

  // ---- zone editing ----

  private newZone(name: string, expanded: boolean): ShippingZoneV2 {
    return {
      id: null,
      zoneName: name,
      isEnabled: true,
      expanded,
      countries: [this.isPk ? 'PK' : 'USA'],
      // default to every state / province selected (shows "NN states selected", matching V1)
      states: (this.isPk ? this.pkProvinces : this.usStates).map((s) => s.code),
      allStates: true,
      rateType: 'flat',
      flatCost: this.isPk ? 300 : 5,
      pickup: false,
      pickupCost: 0,
      uspsEnabled: true,
      uspsServices: [],
      markup: '0',
      fallback: '9.00',
      flatRateId: null,
      storePickUpId: null,
      uspsId: null,
      uspsUserID: null,
    };
  }

  addZone(): void {
    this.zones.forEach((z) => (z.expanded = false));
    this.zones.push(this.newZone(this.zones.length === 0 ? 'Default Zone' : 'New Zone', true));
  }

  toggleExpand(zone: ShippingZoneV2): void {
    zone.expanded = !zone.expanded;
  }

  removeZone(zone: ShippingZoneV2): void {
    this.confirmationService.confirm({
      header: 'Delete Zone',
      message: `Delete the "${zone.zoneName}" zone?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.deleteZone(zone),
    });
  }

  private deleteZone(zone: ShippingZoneV2): void {
    const zoneId = zone.id != null && String(zone.id).trim() !== '' ? String(zone.id).trim() : null;
    // Unsaved zone → just drop it locally.
    if (!zoneId) {
      this.zones = this.zones.filter((z) => z !== zone);
      return;
    }
    this.appBaseDataService.showSpinner();
    this.businessSetupService.deleteShippingConfiguration(zoneId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any) => {
          this.appBaseDataService.hideSpinner();
          if (res?.success === false) {
            this.connMessageService.showToastMessage('Error deleting zone', 'error');
            return;
          }
          this.connMessageService.showToastMessage('Zone deleted successfully!', 'success');
          this.loadShippingConfiguration();
        },
        () => {
          this.appBaseDataService.hideSpinner();
          this.connMessageService.showToastMessage('Error deleting zone', 'error');
        },
      );
  }

  toggleUspsService(zone: ShippingZoneV2, value: string): void {
    zone.uspsServices = zone.uspsServices.includes(value)
      ? zone.uspsServices.filter((v) => v !== value)
      : [...zone.uspsServices, value];
  }

  isUspsServiceActive(zone: ShippingZoneV2, value: string): boolean {
    return zone.uspsServices.includes(value);
  }

  toggleState(zone: ShippingZoneV2, code: string): void {
    zone.states = zone.states.includes(code)
      ? zone.states.filter((c) => c !== code)
      : [...zone.states, code];
  }

  // ---- validation (drives both the toast and the inline per-section messages) ----

  private duplicateNameKeys(): Set<string> {
    const seen = new Set<string>();
    const dupes = new Set<string>();
    for (const z of this.zones) {
      const key = (z.zoneName || '').trim().toLowerCase();
      if (!key) continue;
      if (seen.has(key)) dupes.add(key);
      seen.add(key);
    }
    return dupes;
  }

  zoneNameError(zone: ShippingZoneV2): string | null {
    const key = (zone.zoneName || '').trim().toLowerCase();
    if (!key) return 'Zone name is required.';
    if (this.duplicateNameKeys().has(key)) return 'A zone with that name already exists — choose a unique name.';
    return null;
  }

  flatCostError(zone: ShippingZoneV2): string | null {
    if (zone.rateType === 'flat' && !(Number(zone.flatCost) > 0)) {
      return 'Shipping cost must be greater than 0.';
    }
    return null;
  }

  uspsServicesError(zone: ShippingZoneV2): string | null {
    if (zone.rateType === 'live' && zone.uspsEnabled && zone.uspsServices.length === 0) {
      return 'Select at least one USPS service.';
    }
    return null;
  }

  private validate(): boolean {
    for (const z of this.zones) {
      if (this.zoneNameError(z)) {
        this.connMessageService.showToastMessage(this.zoneNameError(z), 'warn');
        return false;
      }
      if (this.flatCostError(z)) {
        this.connMessageService.showToastMessage(this.flatCostError(z), 'warn');
        return false;
      }
      if (this.uspsServicesError(z)) {
        this.connMessageService.showToastMessage(this.uspsServicesError(z), 'warn');
        return false;
      }
    }
    return true;
  }

  save(): void {
    this.submitted = true;
    if (this.zones.length === 0) {
      this.connMessageService.showToastMessage('Add at least one shipping zone first.', 'warn');
      return;
    }
    if (!this.validate()) return;

    this.saving = true;
    this.appBaseDataService.showSpinner();
    const payload = this.prepareShippingData();
    const hasExisting = this.originalZonesCount > 0;
    const apiCall$ = hasExisting
      ? this.businessSetupService.updateShippingConfiguration(null, payload)
      : this.businessSetupService.saveShippingConfiguration(payload);

    apiCall$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any) => {
          this.saving = false;
          this.appBaseDataService.hideSpinner();
          if (res?.success) {
            this.connMessageService.showToastMessage('Shipping configuration saved successfully!', 'success');
            // The real "a zone was actually saved" signal for the checklist/topbar progress count —
            // the backend's own zones[0].isEnabled is true even on an auto-provisioned zone the
            // merchant never touched, so it can't be used to detect a genuine save.
            this.businessSetupProgressService.markShippingConfigurationSaved();
            this.loadShippingConfiguration();
          } else {
            this.connMessageService.showToastMessage('Error saving shipping configuration', 'error');
          }
        },
        () => {
          this.saving = false;
          this.appBaseDataService.hideSpinner();
          this.connMessageService.showToastMessage('Error saving shipping configuration', 'error');
        },
      );
  }

  private mapRateTypeToShippingType(rateType: 'flat' | 'free' | 'live'): number {
    if (rateType === 'flat') return 1;
    if (rateType === 'free') return 2;
    return 3; // live → USPS / calculated
  }

  private prepareShippingData(): any {
    const businessSetup = GlobalService.userInfo?.businessSetup?.[0];
    const businessAddress = businessSetup?.address?.split(' || ')?.[0]?.trim() || businessSetup?.address || '';

    const zones = this.zones.map((zone) => {
      // Build the { "USA": [stateCodes] } map. Blank selection persists every known code (= whole country).
      const stateCodes = zone.states?.length ? zone.states : this.zoneStates(zone).map((s) => s.code);
      const statesObject: any = {};
      (zone.countries?.length ? zone.countries : ['USA']).forEach((c) => {
        statesObject[c] = stateCodes;
      });

      const isLive = zone.rateType === 'live';
      const isUsps = isLive && zone.uspsEnabled;
      const zoneData: any = {
        isEnabled: zone.isEnabled,
        zoneName: zone.zoneName,
        shippingtype: this.mapRateTypeToShippingType(zone.rateType),
        country: businessSetup?.country || 'US',
        address: businessAddress,
        phone: businessSetup?.phone || '',
        city: businessSetup?.city || '',
        zipCode: businessSetup?.zip || '',
        state: businessSetup?.state || '',
        countries: zone.countries?.length ? zone.countries : ['USA'],
        states: statesObject,
        isSaved: true,
        flatRate: {
          isEnableFlat: zone.rateType === 'flat',
          rate: zone.rateType === 'flat' ? Number(zone.flatCost) : 0,
          isTaxIncluded: true,
          taxRate: 0,
        },
        storePickUp: {
          isEnablePickup: zone.pickup,
          pickupCost: zone.pickup ? Number(zone.pickupCost) : 0,
          isTaxIncluded: true,
          taxRate: 0,
        },
        usps: {
          isEnabledUsps: isUsps,
          postcode: businessSetup?.zip || '',
          enabledServices: isUsps ? zone.uspsServices : [],
        },
      };

      if (zone.id) zoneData.id = zone.id;
      if (zone.flatRateId) zoneData.flatRate.id = zone.flatRateId;
      if (zone.storePickUpId) zoneData.storePickUp.id = zone.storePickUpId;
      if (zone.uspsId) zoneData.usps.id = zone.uspsId;
      if (zone.uspsUserID) zoneData.usps.userID = zone.uspsUserID;

      return zoneData;
    });

    return { zones };
  }

  // header summary shown when a zone is collapsed
  zoneSummary(zone: ShippingZoneV2): string {
    const country = zone.countries?.[0] === 'PK' ? 'Pakistan' : zone.countries?.[0] || 'USA';
    if (zone.rateType === 'flat') return `· ${country} · ${this.currencySymbol}${Number(zone.flatCost).toFixed(2)} flat`;
    if (zone.rateType === 'free') return `· ${country} · Free shipping`;
    return `· ${country} · Live rates`;
  }
}
